import { ReactNode, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Spinner } from '@Components/Spinner/Spinner';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { FetchError } from '@src/utils/fetch-error';
import { useSetError } from '../../../redux/modals';
import { formatChangeSummary } from '../configuration-utils';
import { AuditReport, ClientTypeType, isClientType } from '../digital-audit-constants';
import {
  CLIENT_TYPE_OPTIONS,
  DomainConfig,
  DomainConfigChange,
  VERTICAL_OPTIONS
} from '../domain-config-constants';
import {
  useDomainConfigChanges,
  useDomainConfigResolve,
  useUpdateDomainConfig
} from '../use-domain-config';

const HTTP_CONFLICT = 409;

interface ConfigurationViewProps {
  report: AuditReport | null;
  // Fired after a successful save so the open report's journey re-aligns to
  // the new canonical values.
  onConfigSaved: (clientType: ClientTypeType, vertical: string) => void;
}

export const ConfigurationView = ({ report, onConfigSaved }: ConfigurationViewProps) => (
  <div className='digital-audit-page'>
    <ConfigurationBody report={report} onConfigSaved={onConfigSaved} />
  </div>
);

const ConfigurationBody = ({ report, onConfigSaved }: ConfigurationViewProps) => {
  const resolveQuery = useDomainConfigResolve(report?.input.url ?? '');
  const resolved = resolveQuery.data;
  const changesQuery = useDomainConfigChanges(resolved?.domain);

  if (!report) return <EmptyState>Run a scan to configure its domain.</EmptyState>;
  if (resolveQuery.isLoading) return <Spinner />;
  if (resolveQuery.error) {
    return <EmptyState>Couldn&apos;t load this domain&apos;s configuration.</EmptyState>;
  }
  if (!resolved) return <EmptyState>This domain has no saved configuration yet.</EmptyState>;

  return (
    <>
      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>Configuration · {resolved.domain}</div>
        </div>
        <div className='digital-audit-subtitle'>
          Client type and vertical are shared by everyone who scans this domain. Changes here apply
          to every future scan and are logged below.
        </div>
        <ConfigForm
          // Remount on a fresh server value so the form drafts reset to it.
          key={resolved.config.updated_at}
          domain={resolved.domain}
          config={resolved.config}
          onConfigSaved={onConfigSaved}
        />
      </div>
      <div className='digital-audit-section'>
        <div className='digital-audit-section-head'>
          <div className='digital-audit-section-title'>Change log</div>
        </div>
        <ChangeLog
          changes={changesQuery.data}
          isLoading={changesQuery.isLoading}
          hasError={Boolean(changesQuery.error)}
        />
      </div>
    </>
  );
};

const EmptyState = ({ children }: { children: ReactNode }) => (
  <div className='digital-audit-empty-state'>{children}</div>
);

interface ConfigFormProps {
  domain: string;
  config: DomainConfig;
  onConfigSaved: (clientType: ClientTypeType, vertical: string) => void;
}

const ConfigForm = ({ domain, config, onConfigSaved }: ConfigFormProps) => {
  const setError = useSetError();
  const updateConfig = useUpdateDomainConfig(domain);
  const [clientType, setClientType] = useState<ClientTypeType>(config.client_type);
  const [vertical, setVertical] = useState(config.vertical);

  const dirty = clientType !== config.client_type || vertical !== config.vertical;

  const handleSave = async () => {
    try {
      const { config: saved } = await updateConfig.mutateAsync({
        client_type: clientType,
        vertical,
        expected_updated_at: config.updated_at
      });
      onConfigSaved(saved.client_type, saved.vertical);
      Mixpanel.track(MxE.RecordSaved, { page: 'Digital Audit', entity: 'domain config' });
    } catch (e) {
      const conflict = e instanceof FetchError && e.status === HTTP_CONFLICT;
      setError({
        title: conflict ? 'Configuration changed' : "Couldn't save configuration",
        message: conflict
          ? 'Someone else changed this domain while you were editing. The latest values have been loaded - review them and try again.'
          : `Failed to save: ${e.message}`,
        reportError: e
      });
    }
  };

  return (
    <div className='digital-audit-config-form'>
      <Form.Group>
        <Form.Label>Client Type</Form.Label>
        <Form.Select
          value={clientType}
          onChange={e => {
            if (isClientType(e.target.value)) {
              setClientType(e.target.value);
            }
          }}
        >
          {CLIENT_TYPE_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>Vertical</Form.Label>
        <Form.Select value={vertical} onChange={e => setVertical(e.target.value)}>
          {VERTICAL_OPTIONS.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Button disabled={!dirty} loading={updateConfig.isPending} onClick={handleSave}>
        Save
      </Button>
    </div>
  );
};

interface ChangeLogProps {
  changes: DomainConfigChange[] | undefined;
  isLoading: boolean;
  hasError: boolean;
}

const ChangeLog = ({ changes, isLoading, hasError }: ChangeLogProps) => {
  if (isLoading) return <Spinner />;
  if (hasError) return <EmptyState>Couldn&apos;t load the change log.</EmptyState>;
  if (!changes || changes.length === 0) return <EmptyState>No changes yet.</EmptyState>;
  return (
    <ul className='digital-audit-config-changes'>
      {changes.map(change => (
        <ChangeLogEntry key={change.id} change={change} />
      ))}
    </ul>
  );
};

interface ChangeLogEntryProps {
  change: DomainConfigChange;
}

const ChangeLogEntry = ({ change }: ChangeLogEntryProps) => (
  <li>
    <span>{formatChangeSummary(change)}</span>
    <span className='digital-audit-config-change-meta'>
      {change.changed_by_email} · {format(parseISO(change.changed_at), 'MMM d, yyyy h:mm a')}
    </span>
  </li>
);
